import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as TestIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  CheckCircle as CheckIcon,
  CloudQueue as ProviderIcon,
  Memory as ModelIcon,
  Thermostat as TemperatureIcon,
  Notes as MaxTokensIcon,
  InfoOutlined as DescriptionIcon,
  Bolt as StreamingIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { seededConfigName, seededConfigDescription } from '../../utils/config/seededConfigText';
import { ChatConfigResponse } from '@/api/ApiClient';
import { ConfigDetailRow } from '../ui/ConfigDetailRow';

interface AllConfigsTabProps {
  configs: ChatConfigResponse[];
  onAdd: () => void;
  onEdit: (config: ChatConfigResponse) => void;
  onTest: (config: ChatConfigResponse) => void;
  onSetDefault: (config: ChatConfigResponse) => void;
  onDelete: (configId: string) => void;
}

interface ConfigCardProps {
  config: ChatConfigResponse;
  onEdit: (config: ChatConfigResponse) => void;
  onTest: (config: ChatConfigResponse) => void;
  onSetDefault: (config: ChatConfigResponse) => void;
  onDelete: (configId: string) => void;
}

function ConfigCard({ config, onEdit, onTest, onSetDefault, onDelete }: ConfigCardProps) {
  const { t } = useTranslation();

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: { xs: 2, md: 3 }, '&:last-child': { pb: { xs: 2, md: 3 } } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'flex-start' },
            gap: { xs: 2, md: 3 },
          }}
        >
          {/* LEFT: title + badges */}
          <Box sx={{ width: { xs: '100%', md: 200 }, flexShrink: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, lineHeight: 1.3 }}>
              {seededConfigName(config.display_name, t) || `${config.provider_type} Configuration`}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {config.is_default && (
                <Chip
                  label={t('chatConfig.currentConfig.default')}
                  color="primary"
                  size="small"
                  icon={<StarIcon />}
                />
              )}
              {config.is_active && (
                <Chip
                  label={t('chatConfig.currentConfig.active')}
                  color="success"
                  size="small"
                  icon={<CheckIcon />}
                />
              )}
            </Box>
          </Box>

          {/* Vertical divider — md+ only */}
          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: 'none', md: 'block' } }}
          />

          {/* MIDDLE: parameter detail rows */}
          <Box sx={{ flexGrow: 1 }}>
            <Stack spacing={1.25}>
              <ConfigDetailRow
                icon={<ProviderIcon fontSize="small" />}
                label={t('chatConfig.currentConfig.provider')}
                value={config.provider_type}
              />
              <ConfigDetailRow
                icon={<ModelIcon fontSize="small" />}
                label={t('chatConfig.currentConfig.model')}
                value={config.model}
              />
              <ConfigDetailRow
                icon={<TemperatureIcon fontSize="small" />}
                label={t('chatConfig.currentConfig.temperature')}
                value={config.temperature}
              />
              <ConfigDetailRow
                icon={<MaxTokensIcon fontSize="small" />}
                label={t('chatConfig.currentConfig.maxTokens')}
                value={config.max_tokens}
              />
              <ConfigDetailRow
                icon={<StreamingIcon fontSize="small" />}
                label={t('chatConfig.currentConfig.streaming')}
                value={config.streaming ? t('common.yes') : t('common.no')}
              />
              {config.description && (
                <ConfigDetailRow
                  icon={<DescriptionIcon fontSize="small" />}
                  label={t('chatConfig.currentConfig.description')}
                  value={seededConfigDescription(config.description, t)}
                  alignItems="flex-start"
                />
              )}
            </Stack>
          </Box>

          {/* RIGHT: action buttons */}
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                bgcolor: 'action.hover',
                borderRadius: 2,
                p: 0.5,
              }}
            >
              <IconButton
                size="small"
                onClick={() => onTest(config)}
                title={t('chatConfig.actions.testConfiguration')}
                aria-label={t('chatConfig.actions.testConfiguration')}
              >
                <TestIcon fontSize="small" />
              </IconButton>
              {!config.is_default && (
                <IconButton
                  size="small"
                  onClick={() => onSetDefault(config)}
                  title={t('chatConfig.actions.setAsDefault')}
                  aria-label={t('chatConfig.actions.setAsDefault')}
                  color="primary"
                >
                  <StarBorderIcon fontSize="small" />
                </IconButton>
              )}
              {config.id !== 'env-default' && (
                <IconButton
                  size="small"
                  onClick={() => onEdit(config)}
                  title={t('chatConfig.actions.editConfiguration')}
                  aria-label={t('chatConfig.actions.editConfiguration')}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
              {config.id && config.id !== 'env-default' && (
                <IconButton
                  size="small"
                  onClick={() => onDelete(config.id!)}
                  title={t('chatConfig.actions.deleteConfiguration')}
                  aria-label={t('chatConfig.actions.deleteConfiguration')}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function AllConfigsTab({
  configs,
  onAdd,
  onEdit,
  onTest,
  onSetDefault,
  onDelete,
}: AllConfigsTabProps) {
  const { t } = useTranslation();

  return (
    <Box>
      {/* Header row — unchanged */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">{t('chatConfig.tabs.allConfigurations')}</Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={onAdd}>
          {t('chatConfig.actions.addConfiguration')}
        </Button>
      </Box>

      {/* One card per config */}
      <Stack spacing={2}>
        {configs.map((config, index) => (
          <ConfigCard
            key={config.id ?? index}
            config={config}
            onEdit={onEdit}
            onTest={onTest}
            onSetDefault={onSetDefault}
            onDelete={onDelete}
          />
        ))}
      </Stack>
    </Box>
  );
}
